import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { timer } from 'rxjs';
import { Audience } from '../../audience/components/audience.model';
import { AgentMessage, PendingConfirmation, TemplateSelection, TurnRating } from '../agent-conversation.model';
import { mergeAudiences } from '../agent-audiences';
import { TemplateCard, mergeTemplateCards } from '../agent-template-cards';
import { MessageSegment, segmentMentions } from '../agent-composer-dom';
import { openCampaignPreview, openTranscriptLink } from '../agent-transcript-link';

interface RenderedMessage {
  message: AgentMessage;
  // True while streaming with nothing being produced (before the first block, or between tool calls).
  waiting: boolean;
  // A tool call failed and wasn't recovered; drives a banner from tool state, not the model's text.
  hadFailedTool: boolean;
  // Merged, de-duplicated templates from all campaignTemplates_list calls in the turn.
  templateCards: TemplateCard[];
  // True only for the live turn's picker; older ones render disabled.
  templatePickerActive: boolean;
  // i18n key for an errored turn, mapped from the error code.
  errorTextKey: string;
  // Retry is offered only on the last turn (retry() re-runs the last request), and never for an
  // approval resume (re-running could double-execute the tool).
  retryable: boolean;
  // Assistant text for the copy button; '' hides it (user turns, or still streaming).
  copyText: string;
  // Merged, de-duplicated audiences from all audiences_list calls in the turn; each opens the flyout.
  audiences: Audience[];
  // A settled assistant answer the user can rate: has a backend turn id and isn't a pending
  // confirmation.
  ratable: boolean;
  // The current rating, for active-thumb state.
  rating: TurnRating | undefined;
  // Chip/text runs for a user turn's text; empty for assistant turns (theirs renders as markdown).
  segments: RenderedSegment[];
}

interface RenderedSegment extends MessageSegment {
  // The campaign id when this segment is a campaign chip, else null. Precomputed so the template
  // branches on it without per-item logic.
  campaignId: number | null;
}

@Component({
  selector: 'app-agent-message-list',
  templateUrl: './agent-message-list.component.html',
  styleUrls: ['./agent-message-list.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentMessageListComponent {
  readonly messages = input.required<readonly AgentMessage[]>();
  readonly approve = output<PendingConfirmation>();
  readonly deny = output<PendingConfirmation>();
  readonly pickTemplate = output<TemplateSelection>();
  readonly openAudience = output<Audience>();
  readonly retry = output<void>();
  readonly rate = output<{ messageId: string; rating: TurnRating }>();

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  // Index of the message whose copy button was just clicked, so it can flash a "copied" state.
  protected readonly copiedIndex = signal<number | null>(null);

  protected readonly renderedMessages = computed<RenderedMessage[]>(() => {
    const list = this.messages();
    const lastIndex = list.length - 1;
    return list.map((message, index) => ({
      message,
      waiting: isWaiting(message),
      hadFailedTool: hasFailedTool(message),
      templateCards: templateCardsFor(message),
      templatePickerActive: index === lastIndex && message.role === 'assistant' && message.status === 'complete',
      errorTextKey: errorKeyFor(message.errorCode),
      copyText: copyTextFor(message),
      audiences: audiencesFor(message),
      retryable:
        index === lastIndex &&
        message.status === 'error' &&
        !message.resumedApproval &&
        !NON_RETRYABLE_ERROR_CODES.has(message.errorCode ?? ''),
      ratable: isRatable(message),
      rating: message.rating,
      segments:
        message.role === 'user'
          ? segmentMentions(message.text ?? '', message.mentions).map((segment) => ({
              ...segment,
              campaignId: segment.mention?.kind === 'campaign' ? Number(segment.mention.id) : null,
            }))
          : [],
    }));
  });

  protected onMentionClick(mention: NonNullable<MessageSegment['mention']>): void {
    if (mention.kind !== 'campaign') {
      return;
    }
    openCampaignPreview(this.dialog, Number(mention.id), mention.label);
  }

  protected onRate(item: RenderedMessage, rating: TurnRating): void {
    const messageId = item.message.messageId;
    if (messageId) {
      this.rate.emit({ messageId, rating });
    }
  }

  protected copy(index: number, text: string): void {
    // Can reject on denied permission or an unfocused document; swallow so it doesn't surface.
    navigator.clipboard?.writeText(text).catch(() => {});
    this.copiedIndex.set(index);
    timer(1500)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.copiedIndex.set(null));
  }

  protected onContentClick(event: MouseEvent): void {
    openTranscriptLink(event, this.dialog, this.router);
  }
}

// Merge all completed campaignTemplates_list results into one card list (the model may search twice).
function templateCardsFor(message: AgentMessage): TemplateCard[] {
  if (message.role !== 'assistant') {
    return [];
  }
  const contents = (message.blocks ?? [])
    .filter(
      (block) =>
        block.kind === 'tool_use' && block.toolName === 'campaignTemplates_list' && block.toolStatus === 'done',
    )
    .map((block) => block.content);
  return mergeTemplateCards(contents);
}

// Audiences render as a clickable list only when browsing them was the turn's final tool action
// (the user asked to see them). When the model lists audiences only to resolve an id for a later
// mutation, that mutation is the last call and the list stays hidden. Merged across calls.
function audiencesFor(message: AgentMessage): Audience[] {
  if (message.role !== 'assistant') {
    return [];
  }
  const toolCalls = (message.blocks ?? []).filter((block) => block.kind === 'tool_use');
  const last = toolCalls[toolCalls.length - 1];
  if (!last || last.toolName !== 'audiences_list' || last.toolStatus !== 'done') {
    return [];
  }
  const contents = toolCalls
    .filter((block) => block.toolName === 'audiences_list' && block.toolStatus === 'done')
    .map((block) => block.content);
  return mergeAudiences(contents);
}

// Known error codes to specific message keys; everything else uses the generic message.
const ERROR_MESSAGE_KEYS: Record<string, string> = {
  tool_call_limit_exceeded: 'agent.error-detail.tool-limit',
  turn_timeout: 'agent.error-detail.timeout',
  usage_limit_exceeded: 'agent.error-detail.usage-limit',
  insufficient_tier: 'agent.error-detail.insufficient-tier',
  disallowed_surface: 'agent.error-detail.unavailable-here',
  tool_not_found: 'agent.error-detail.unavailable',
  transient_error: 'agent.error-detail.temporary',
  message_too_long: 'agent.error-detail.message-too-long',
  invalid_arguments: 'agent.error-detail.invalid-action',
  confirmation_not_found: 'agent.error-detail.approval-expired',
  confirmation_resolved: 'agent.error-detail.already-answered',
  confirmation_mismatch: 'agent.error-detail.approval-mismatch',
  confirmation_unavailable: 'agent.error-detail.approval-unavailable',
};

// Retry resends the same message, so these would only fail again.
const NON_RETRYABLE_ERROR_CODES = new Set([
  'message_too_long',
  'invalid_arguments',
  'confirmation_not_found',
  'confirmation_resolved',
  'confirmation_mismatch',
]);

function errorKeyFor(code: string | undefined): string {
  return (code && ERROR_MESSAGE_KEYS[code]) || 'agent.error';
}

// Plain text of a settled assistant turn for copy; empty for user turns or while streaming.
function copyTextFor(message: AgentMessage): string {
  if (message.role !== 'assistant' || message.status === 'streaming') {
    return '';
  }
  return (message.blocks ?? [])
    .filter((block) => block.kind === 'text' && !!block.text)
    .map((block) => block.text)
    .join('\n\n')
    .trim();
}

function hasFailedTool(message: AgentMessage): boolean {
  if (message.role !== 'assistant') {
    return false;
  }
  // A failed call counts only if the same tool wasn't later retried successfully (the model
  // self-corrects in place, e.g. a wrong id then a right one).
  const toolCalls = (message.blocks ?? []).filter((block) => block.kind === 'tool_use');
  return toolCalls.some(
    (failed, index) =>
      failed.toolStatus === 'error' &&
      !toolCalls.slice(index + 1).some((later) => later.toolName === failed.toolName && later.toolStatus === 'done'),
  );
}

function isWaiting(message: AgentMessage): boolean {
  if (message.role !== 'assistant' || message.status !== 'streaming') {
    return false;
  }
  const blocks = message.blocks ?? [];
  if (blocks.length === 0) {
    return true;
  }
  const last = blocks[blocks.length - 1];
  return (
    last.kind === 'tool_use' &&
    (last.toolStatus === 'done' ||
      last.toolStatus === 'error' ||
      last.toolStatus === 'refused' ||
      last.toolStatus === 'skipped')
  );
}

// A settled assistant answer the user can rate. Needs the backend turn id (from message_start), and
// excludes turns that are still awaiting a confirmation.
function isRatable(message: AgentMessage): boolean {
  return (
    message.role === 'assistant' &&
    (message.status === 'complete' || message.status === 'stopped') &&
    !!message.messageId &&
    !message.confirmation
  );
}
